import type { FetchResult } from '@apollo/client';
import type { ReactNode } from 'react';

import type { BaseValues, PresentationStatus } from './types';
import type { DeletePresentationMutation, Presentation } from '../../../generated/graphql-manager';

import { createContext, useContext, useRef, useState } from 'react';

import { PresentationStatusPoller } from './presentation-status-poller';
import {
  GetWebcastPresentationsDocument as GET_WEBCAST_PRESENTATIONS,
  useCreatePresentationMutation,
  useDeletePresentationMutation,
} from '../../../generated/graphql-manager';
import { updateObjectInArray, uploadFile } from '../../../utils';
import { useProgressContext } from '../progress-context/progress-context';

type UploadPresentation = ({
  webcastId,
  language,
  groupId,
  file,
  onSuccess,
}: {
  webcastId: BaseValues['webcastId'];
  language: Presentation['language'];
  groupId?: BaseValues['groupId'];
  file: File;
  onSuccess: () => void;
}) => void;

type Context = {
  presentationStatuses: PresentationStatus[];
  uploadPresentation: UploadPresentation;
  deletePresentation: (presentation: PresentationStatus) => Promise<FetchResult<DeletePresentationMutation>>;
  presentationLoading: boolean;
};

const createPresentationStatus = (baseValues: BaseValues): PresentationStatus => ({
  uploadProgress: 0,
  totalSlides: null,
  processedSlides: null,
  ...baseValues,
});

const PresentationsProcessorContext = createContext<Context>({
  presentationStatuses: [],
  uploadPresentation: () => {},
  deletePresentation: () => Promise.resolve({ data: null }),
  presentationLoading: false,
});

export const usePresentationsProcessor = () => useContext(PresentationsProcessorContext);

export function PresentationsProcessorProvider({ children }: { children: ReactNode }) {
  const xhrRef = useRef<Record<string, XMLHttpRequest>>({});
  const { addProgressIndicator, updateStatus, updateProgress, cancelProgressIndicator } = useProgressContext();
  const [presentationStatuses, setPresentationStatuses] = useState<PresentationStatus[]>([]);

  const [createPresentationMutation, createPresentationMutationResponse] = useCreatePresentationMutation();

  const updatePresentationStatus = (updatedStatus: PresentationStatus) => {
    setPresentationStatuses((statuses) => {
      const existingStatus: PresentationStatus | undefined = statuses.find(
        (currentStatus) => currentStatus.id === updatedStatus.id
      );

      const newStatus = createPresentationStatus({ ...(existingStatus || {}), ...updatedStatus });

      return updateObjectInArray(statuses, newStatus, 'id');
    });
  };

  const uploadPresentation: UploadPresentation = ({ webcastId, language, groupId, file, onSuccess }) => {
    createPresentationMutation({
      variables: {
        createPresentationInput: {
          webcastId,
          language,
          sourceFileName: file.name,
        },
        groupId,
      },
      onCompleted: (data) => {
        if (data.createPresentation.__typename !== 'CreatePresentationSuccess') return;

        const {
          id,
          groupId,
          uploadLink: { url, token },
        } = data.createPresentation.presentation;

        updatePresentationStatus({ id, webcastId, language, groupId });
        addProgressIndicator({ id, fileName: file.name, status: 'TRANSFERRING', progressPercentage: 0 });

        xhrRef.current[id] = uploadFile({
          file,
          token,
          url,
          onProgress: (uploadProgress) => {
            updateProgress(id, uploadProgress / 2);
            updatePresentationStatus({ id, webcastId, language, groupId, uploadProgress });
          },
          onSuccess: () => {
            onSuccess?.();
          },
          onError: () => {
            updateStatus(id, 'FAILED');
          },
        });
      },
    });
  };

  const [deletePresentationMutation, deletePresentationMutationResponse] = useDeletePresentationMutation();

  const deletePresentation = ({ id, webcastId, language, groupId }: PresentationStatus) => {
    cancelProcessing(id);

    return deletePresentationMutation({
      variables: {
        deletePresentationInput: {
          webcastId,
          language,
          groupId,
        },
      },
      refetchQueries: [{ query: GET_WEBCAST_PRESENTATIONS, variables: { webcastId } }],
    });
  };

  const cancelProcessing = (id: Presentation['id']) => {
    xhrRef.current[id]?.abort();
    removePresentationStatus(id);
    cancelProgressIndicator(id);
  };

  const removePresentationStatus = (id: Presentation['id']) => {
    setPresentationStatuses((state) => state.filter((status) => status.id !== id));
  };

  return (
    <PresentationsProcessorContext.Provider
      value={{
        presentationStatuses,
        uploadPresentation,
        deletePresentation,
        presentationLoading: createPresentationMutationResponse.loading || deletePresentationMutationResponse.loading,
      }}
    >
      {presentationStatuses.map((presentationStatus) => (
        <PresentationStatusPoller
          key={presentationStatus.id}
          id={presentationStatus.id}
          webcastId={presentationStatus.webcastId}
          onFinish={removePresentationStatus}
          onFail={removePresentationStatus}
        />
      ))}

      {children}
    </PresentationsProcessorContext.Provider>
  );
}
